import { BroadcastMessageType } from '../types'
import type { MaybeRefOrGetter } from '#imports'

import {
    ref,
    computed,
    toValue,
    useCookie,
    useRequestURL,
    useReload,
    useDayjs,
    useUserInfo,
    useCoreConfig,
    useDevice,
    useSharedBroadcast,
    useQuery,
    useUserAuthClient,
    useTracking,
    createSharedComposable
} from '#imports'

export const useWebauthn = createSharedComposable(() => {
    const { site } = useCoreConfig()
    const { isLoggedIn } = useUserInfo()
    const config = useCoreConfig()
    const dayjs = useDayjs()
    const url = useRequestURL()

    const { userAgent, isIos, isAndroid } = useDevice()
    const { sendMessage } = useSharedBroadcast()

    const { client } = useUserAuthClient()
    const reload = useReload()
    const { trackEvent } = useTracking()

    // State - WebAuthn Core
    const authenticationOptions = ref()
    const registrationOptions = ref()
    const registrationState = ref<{
        loading: boolean
        error: string | null
        isRegistered: boolean
    }>({
        loading: false,
        error: null,
        isRegistered: false
    })

    // State - UI
    const isModalShown = ref(false)

    // State - Cookies and persistence
    const passkeyStatus = useCookie<string>('passkey_status', {
        maxAge: dayjs.duration({ days: 30 }).asSeconds()
    })

    // State - Mobile app
    const isMobileApp = import.meta.client ? 'ReactNativeWebView' in window : false
    const appPasskeyState = ref<'CREATED' | 'NONE' | 'DISMISSED' | null>(null)

    // State - Web passkey support
    const webPasskeySupported = ref(false)

    if (import.meta.client && !isMobileApp && config.webauthn?.enabled) {
        (async () => {
            try {
                const supported = await isPlatformPassKeySupported()
                webPasskeySupported.value = !!supported
            }
            catch {
                webPasskeySupported.value = false
            }
        })()
    }

    // Computed properties
    const isMobileAppAndRequiresUpdate = computed(() => {
        const getAppVersion = () => {
            const match = userAgent.match(/(PlategoApp)\/([0-9.]+)/)
            return match ? match[2] : null
        }

        const getVersionParts = (v: string): { major: number, minor: number, patch: number } => {
            const parts = v.split('.')

            return {
                major: Number(parts[0]) || 0,
                minor: Number(parts[1]) || 0,
                patch: Number(parts[2]) || 0
            }
        }

        const version = getAppVersion() // 2.1.4

        if (!version) return

        const minVersionRequired = config.app?.minVersionRequired ?? '2.1.5' // App version that changes to semantic versioning

        // If the app still uses the old versioning
        if (!version.includes('.')) { // e.g. 7 - Old way of versioning
            if (minVersionRequired.includes('.')) return true // New semantic versioning - if the config has a semantic version set, then the user needs to update the app

            // Both use old versioning - compare as numbers
            // User needs update if their version is less than required
            return Number(version) < Number(minVersionRequired)
        }

        // App uses semantic versioning but web config hasn't been updated yet
        // Don't prompt for update in this case
        if (!minVersionRequired.includes('.')) return false

        // Both use semantic versioning - compare properly
        const appParts = getVersionParts(version)
        const requiredParts = getVersionParts(minVersionRequired)

        if (appParts.major < requiredParts.major) return true
        if (appParts.major > requiredParts.major) return false

        if (appParts.minor < requiredParts.minor) return true
        if (appParts.minor > requiredParts.minor) return false

        return appParts.patch < requiredParts.patch
    })

    const canLoginWithWebPasskey = computed(() => {
        return !isMobileApp && webPasskeySupported.value && passkeyStatus.value === 'registered'
    })

    const shouldShowPassKeyRegistrationModal = computed(() => {
        if (!toValue(isLoggedIn)) return

        if (isMobileApp) {
            return appPasskeyState.value === 'NONE'
        }

        return false // Dont prompt web - soft launch
    })

    // Core WebAuthn Functions
    async function isPlatformPassKeySupported() {
        const { browserSupportsWebAuthn, platformAuthenticatorIsAvailable } = await import('@simplewebauthn/browser')
        return config.webauthn?.enabled && browserSupportsWebAuthn() && await platformAuthenticatorIsAvailable()
    }

    async function fetchRegistrationOptions(options?: { force: boolean }) {
        const { force = false } = options || {}
        // Return cached options if available and not forcing refresh
        if (registrationOptions.value && !force) {
            return registrationOptions.value
        }

        const res = await client.GET('/v1/public/pass-key/registration-options', {
            params: {
                query: {
                    brand: site.brand
                }
            },
        })
        registrationOptions.value = res?.data

        return res?.data
    }

    function clearRegistrationOptions() {
        registrationOptions.value = undefined
    }

    async function verifyRegistration(attResp: any) {
        registrationState.value.error = null

        try {
            await client.POST('/v1/public/pass-key/verify-registration', {
                body: attResp
            })
        }
        catch {
            await trackEvent({
                event_name: 'passkey_register_failed',
                event_type: 'Click',
                meta_data: [
                    { category: 'Passkey' },
                ],
            })

            markPassKeyAsRejected()
            return
        }

        await trackEvent({
            event_name: 'passkey_register_completed',
            event_type: 'Click',
            meta_data: [
                { category: 'Passkey' },
            ],
        })

        markPassKeyAsRegistered()
    }

    async function registerPassKey() {
        registrationState.value.error = null

        await trackEvent({
            event_name: 'passkey_register',
            event_type: 'Click',
            meta_data: [
                { category: 'Passkey' },
            ],
        })

        const options: any = await fetchRegistrationOptions()

        try {
            const { startRegistration } = await import('@simplewebauthn/browser')
            const attResp = await startRegistration(options)
            await verifyRegistration(attResp)
        }
        catch (err: any) {
            if (err.code === 'ERROR_AUTHENTICATOR_PREVIOUSLY_REGISTERED') {
                await trackEvent({
                    event_name: 'passkey_register_completed',
                    event_type: 'Click',
                    meta_data: [
                        { category: 'Passkey' },
                    ],
                })

                markPassKeyAsRegistered()
            }
            else {
                await trackEvent({
                    event_name: 'passkey_register_failed',
                    event_type: 'Click',
                    meta_data: [
                        { category: 'Passkey' },
                    ],
                })

                markPassKeyAsRejected()
            }

            closeModal()
        }
    }

    async function getAuthenticationOptions() {
        if (authenticationOptions.value) {
            return authenticationOptions.value
        }

        try {
            const res = await client.GET('/v1/public/pass-key/authentication-options', {
                params: {
                    query: {
                        brand: site.brand
                    }
                },
            })
            authenticationOptions.value = res?.data
            return authenticationOptions.value
        }
        catch {
            // Do nothing
        }
    }

    async function authenticateWithWebAuthn(authOptions: any) {
        registrationState.value.error = null

        if (!authOptions) {
            registrationState.value.error = 'core.defaultErrorMessage'
            setLoadingState(false)
            return
        }

        try {
            await client.POST('/v1/public/pass-key/verify-authentication', {
                body: {
                    ...authOptions,
                    brand: site.brand
                },
            })
        }
        catch {
            registrationState.value.error = 'core.defaultErrorMessage'
            setLoadingState(false)

            await trackEvent({
                event_name: 'login_passkey_failed',
                event_type: 'Click',
                meta_data: [
                    { category: 'Login' },
                ],
            })

            return
        }

        await trackEvent({
            event_name: 'login_passkey_completed',
            event_type: 'Click',
            meta_data: [
                { category: 'Login' },
            ],
        })

        await reload(`${url.origin}${url.pathname}`)
    }

    async function initiatePassKeyLogin() {
        await trackEvent({
            event_name: 'login_passkey',
            event_type: 'Click',
            meta_data: [
                { category: 'Login' },
            ],
        })

        try {
            registrationState.value.error = null

            const authenticationOptions = await getAuthenticationOptions()

            if (!authenticationOptions) {
                setLoadingState(false)
                return
            }

            if (isMobileApp) {
                window.ReactNativeWebView.postMessage(JSON.stringify({
                    type: 'pass_keys_authenticate_with_pass_key',
                    authenticationOptions
                }))
                return
            }

            const isSupported = await isPlatformPassKeySupported()

            if (!isSupported) {
                setLoadingState(false)
                return
            }

            const { startAuthentication: startAuth } = await import('@simplewebauthn/browser')
            await startAuth(authenticationOptions).then(authenticateWithWebAuthn)
        }
        catch {
            await trackEvent({
                event_name: 'login_passkey_failed',
                event_type: 'Click',
                meta_data: [
                    { category: 'Login' },
                ],
            })

            setLoadingState(false)
        }
    }

    // Data fetching
    function useGetPasskeys(options: { lazy?: boolean, server?: boolean, enabled?: MaybeRefOrGetter<boolean> } = {}) {
        const { lazy = true, server = false, enabled = true } = options

        const query = useQuery(async () => {
            const response = await client.GET('/v1/public/pass-key', {})
            return response
        }, {
            key: 'webauthn:passkeys',
            lazy,
            server,
            enabled,
        })

        return query
    }

    // UI Management Functions
    function closeModal() {
        registrationState.value.error = null
        isModalShown.value = false
        document.dispatchEvent(new CustomEvent('closePassKeyModal'))
    }

    function showPassKeysModal() {
        document.dispatchEvent(new CustomEvent('showPassKeysModal'))
        isModalShown.value = true
    }

    // State Management Functions
    function markPassKeyAsRegistered() {
        passkeyStatus.value = 'registered'
        registrationState.value.isRegistered = true

        // Clear registration options to ensure fresh ones are fetched next time
        clearRegistrationOptions()

        sendMessage({ type: BroadcastMessageType.PASSKEY_REGISTERED })

        if (isMobileApp) {
            setAppPasskeyState('CREATED')
        }

        setTimeout(() => {
            closeModal()
        }, 5000)
    }

    function markPassKeyAsRejected(customErrorMessage?: string) {
        registrationState.value.error = customErrorMessage ?? 'core.webauthn.registrationDialog.errorMessage'
        passkeyStatus.value = 'denied'

        sendMessage({ type: BroadcastMessageType.PASSKEY_REJECTED })
    }

    async function checkAndRestorePasskeyStatus(): Promise<boolean> {
        if (passkeyStatus.value === 'registered') return true

        try {
            const response = await client.GET('/v1/public/pass-key', {})
            if (hasPasskeyOnDevice(response?.data || [])) {
                passkeyStatus.value = 'registered'
                return true
            }
        }
        catch {
            // Silently fail
        }
        return false
    }

    function markPassKeyAsDismissed() {
        passkeyStatus.value = 'dismissed'
        closeModal()
    }

    function setLoadingState(loading: boolean) {
        registrationState.value.loading = loading
    }

    // Mobile App Functions
    function clearAppPasskeyState() {
        if (!isMobileApp) return
        setAppPasskeyState('NONE')
    }

    function setAppPasskeyState(passkeyStatus: 'CREATED' | 'NONE' | 'DISMISSED') {
        if (!isMobileApp) return

        const stateMap = {
            CREATED: 'pass_keys_registered',
            DISMISSED: 'pass_keys_dismissed',
            NONE: 'pass_keys_cleared',
        }

        appPasskeyState.value = passkeyStatus
        window.ReactNativeWebView.postMessage(JSON.stringify({ type: stateMap[passkeyStatus] }))
    }

    function handleNativePasskeyError(error: string) {
        registrationState.value.error = error
        setLoadingState(false)
    }

    function setupMobileAppIntegration() {
        if (!import.meta.client || !config.webauthn?.enabled || !isMobileApp) return

        window.passKeyFunctions = {
            showModal: () => { showPassKeysModal() },
            closeModal: () => { closeModal() },
            rejectPassKey: () => { markPassKeyAsRejected() },
            passKeyRegistered: () => { markPassKeyAsRegistered() },
            verifyRegistration: async response => verifyRegistration(response),
            initiatePassKeyLogin: () => { initiatePassKeyLogin() },
            setAppPasskeyStatus: (passkeyStatus: 'CREATED' | 'NONE' | 'DISMISSED') => { setAppPasskeyState(passkeyStatus) },
            loginWithAuthenticationResponse: async authOptions => authenticateWithWebAuthn(authOptions),
            handleNativePasskeyError: (error: string) => { handleNativePasskeyError(error) },
            setPasskeyLoading: (loading: boolean) => { setLoadingState(loading) },
        }

        if (shouldShowPassKeyRegistrationModal.value && isMobileApp) {
            window?.ReactNativeWebView?.postMessage(JSON.stringify({ type: 'pass_keys_initiate' }))
        }
    }

    // Utility Functions
    function getMatchingPasskeysOnDevice(passkeys: Array<{ name: string, id: string | number, created_at: string | null }>): Array<{ name: string, id: string | number, created_at: string | null }> {
        if (!passkeys?.length) return []

        const userAgentLower = userAgent.toLowerCase()
        const currentDeviceKeywords: string[] = []

        // Add platform keywords
        if (isIos) {
            currentDeviceKeywords.push('ios', 'iphone', 'ipad')
        }
        else if (isAndroid) {
            currentDeviceKeywords.push('android')
            // Add specific Android manufacturer if present
            if (/samsung/.test(userAgentLower)) currentDeviceKeywords.push('samsung')
            if (/pixel/.test(userAgentLower)) currentDeviceKeywords.push('pixel')
            if (/huawei/.test(userAgentLower)) currentDeviceKeywords.push('huawei')
            if (/xiaomi/.test(userAgentLower)) currentDeviceKeywords.push('xiaomi')
            if (/oppo/.test(userAgentLower)) currentDeviceKeywords.push('oppo')
        }
        else {
        // Desktop devices
            if (/mac os x/.test(userAgentLower)) currentDeviceKeywords.push('mac', 'macos')
            if (/windows/.test(userAgentLower)) currentDeviceKeywords.push('windows')
        }

        // Add browser keywords
        if (/chrome/.test(userAgentLower) && !/edg/.test(userAgentLower)) {
            currentDeviceKeywords.push('chrome')
        }
        if (/safari/.test(userAgentLower) && !/chrome/.test(userAgentLower)) {
            currentDeviceKeywords.push('safari')
        }
        if (/edg/.test(userAgentLower)) {
            currentDeviceKeywords.push('edge')
        }
        if (/firefox/.test(userAgentLower)) {
            currentDeviceKeywords.push('firefox')
        }

        if (!currentDeviceKeywords.length) return []

        // Filter passkeys that match the current device
        return passkeys.filter((passkey) => {
            const passkeyNameLower = passkey.name.toLowerCase()

            return currentDeviceKeywords.some(identifier =>
                passkeyNameLower.includes(identifier)
            )
        })
    }

    function hasPasskeyOnDevice(passkeys: Array<{ name: string, id: string | number, created_at: string | null }>): boolean {
        return getMatchingPasskeysOnDevice(passkeys).length > 0
    }

    async function doStartAuthentication(options: any) {
        const { startAuthentication } = await import('@simplewebauthn/browser')
        return startAuthentication(options)
    }

    // Initialization
    function init() {
        setupMobileAppIntegration()
    }

    return {
        // State
        authenticationOptions,
        registrationOptions,
        registrationState,
        isModalShown,
        passkeyStatus,
        appPasskeyState,

        // Computed
        isMobileApp,
        isMobileAppAndRequiresUpdate,
        canLoginWithWebPasskey,
        shouldShowPassKeyRegistrationModal,

        // Core WebAuthn functions
        fetchRegistrationOptions,
        clearRegistrationOptions,
        verifyRegistration,
        registerPassKey,
        getAuthenticationOptions,
        authenticateWithWebAuthn,
        isPlatformPassKeySupported,
        startAuthentication: doStartAuthentication,
        initiatePassKeyLogin,
        useGetPasskeys,

        // UI functions
        closeModal,
        showPassKeysModal,

        // State management
        markPassKeyAsRegistered,
        markPassKeyAsRejected,
        markPassKeyAsDismissed,
        checkAndRestorePasskeyStatus,
        setLoadingState,

        // Mobile app functions
        setAppPasskeyState,
        clearAppPasskeyState,
        handleNativePasskeyError,

        // Utility functions
        getMatchingPasskeysOnDevice,
        hasPasskeyOnDevice,

        // Initialization
        init,
    }
})
